import { ArticleModel } from "../../Article/Article/ArticleModel";
import { ButtonModel } from "../../core/Models/ButtonModel";
import { ImageModel } from "../../core/Models/ImageModel";
import { PageModel } from "../../core/Models/PageModel";
import { FaqModel } from "../../Faq/Faq/FaqModel";
import { ProductModel } from "../ShowcasePanel/ShowcasePanelModel";
import {
  ListPanelModel,
  QuoteModel,
  SolutionsModel,
  VideoModel,
} from "./ListPanelModel";
import { PeopleItemModel } from "./PeopleItem/PeopleItemModel";

class ListPanelUtils {
  static isProductEntity = (
    entity:
      | QuoteModel
      | ProductModel
      | VideoModel
      | PageModel
      | PeopleItemModel
      | SolutionsModel
  ): boolean => {
    return (entity as ProductModel).productName !== undefined;
  };
  static isSolutionsEntity = (
    entity:
      | QuoteModel
      | ProductModel
      | VideoModel
      | PageModel
      | PeopleItemModel
      | SolutionsModel
  ): boolean => {
    return (entity as SolutionsModel)?.component === "SolutionsEntity";
  };

  static isVideoEntity = (
    entity:
      | QuoteModel
      | ProductModel
      | VideoModel
      | PageModel
      | PeopleItemModel
      | SolutionsModel
  ): boolean => {
    return (entity as VideoModel).videoTitle !== undefined;
  };

  static isPageEntity = (
    entity:
      | QuoteModel
      | ProductModel
      | VideoModel
      | PageModel
      | PeopleItemModel
      | SolutionsModel
  ): boolean => {
    return (
      (entity as PageModel).body?.length !== undefined &&
      (entity as PageModel).body?.length !== 0
    );
  };

  static isPeopleEntity = (
    entity:
      | QuoteModel
      | ProductModel
      | VideoModel
      | PageModel
      | PeopleItemModel
      | SolutionsModel
  ): boolean => {
    return (entity as PeopleItemModel).firstName !== undefined;
  };

  static isBenefitEntity = (
    blok: ListPanelModel,
    entity:
      | QuoteModel
      | ProductModel
      | VideoModel
      | PageModel
      | PeopleItemModel
      | SolutionsModel
  ): boolean => {
    return (
      blok.benefits &&
      blok.benefits.length > 0 &&
      ListPanelUtils.isProductEntity(entity) &&
      (entity as ProductModel).benefits?.some((x) =>
        blok.benefits.includes(x.id)
      )
    );
  };

  static getArticle = (
    entity:
      | QuoteModel
      | ProductModel
      | VideoModel
      | PageModel
      | PeopleItemModel
      | SolutionsModel
  ): ArticleModel => {
    return (entity as any).body[0] as ArticleModel;
  };

  static getFaq = (
    entity:
      | QuoteModel
      | ProductModel
      | VideoModel
      | PageModel
      | PeopleItemModel
      | SolutionsModel
  ): FaqModel => {
    return (entity as any).body[0] as FaqModel;
  };

  static getTitle = (
    entity:
      | QuoteModel
      | ProductModel
      | VideoModel
      | PageModel
      | PeopleItemModel
      | SolutionsModel
  ): string => {
    if (this.isProductEntity(entity)) {
      return (entity as ProductModel).productName;
    } else if (this.isSolutionsEntity(entity)) {
      return (entity as SolutionsModel).name;
    } else if (this.isVideoEntity(entity)) {
      return (entity as VideoModel).videoTitle;
    } else if (this.isPageEntity(entity)) {
      return ((entity as any).body[0] as ArticleModel).title;
    }
    return (entity as QuoteModel).quote
      ? `“${(entity as QuoteModel).quote}“`
      : "";
  };

  static getImage = (
    entity:
      | QuoteModel
      | ProductModel
      | VideoModel
      | PageModel
      | PeopleItemModel
      | SolutionsModel
  ): ImageModel => {
    if (this.isProductEntity(entity)) {
      return (entity as ProductModel).productImage;
    } else if (this.isSolutionsEntity(entity)) {
      return (entity as SolutionsModel).image;
    } else if (this.isVideoEntity(entity)) {
      return (entity as VideoModel).image;
    } else if (this.isPageEntity(entity)) {
      return (entity as any).image;
    }
    return (entity as QuoteModel).artistImage;
  };

  static getButtons = (
    entity:
      | QuoteModel
      | ProductModel
      | VideoModel
      | PageModel
      | PeopleItemModel
      | SolutionsModel,
    full_slug?: string
  ): Array<ButtonModel> => {
    if (this.isProductEntity(entity)) {
      const primaryButton = (entity as ProductModel).primaryUrl
        ? {
            link: (entity as ProductModel).primaryUrl,
            label: (entity as ProductModel).primaryLabel,
          }
        : null;
      const secondaryButton = (entity as ProductModel).secondaryUrl
        ? {
            link: (entity as ProductModel).secondaryUrl,
            label: (entity as ProductModel).secondaryLabel,
            color: "#000000",
            borderColor: "#FFFFFF",
          }
        : null;

      return [primaryButton, secondaryButton].filter(
        (button): button is ButtonModel => !!button
      );
    } else if (this.isSolutionsEntity(entity)) {
      return (entity as SolutionsModel).buttons;
    } else if (this.isPageEntity(entity) && full_slug) {
      return [
        {
          link: { cached_url: full_slug, linktype: "story" },
          label: "Read more",
        },
      ];
    }
    return [{ link: { cached_url: "", linktype: "story" }, label: "" }];
  };

  static getDescription = (
    entity:
      | QuoteModel
      | ProductModel
      | VideoModel
      | PageModel
      | PeopleItemModel
      | SolutionsModel
  ): string => {
    if (this.isProductEntity(entity)) {
      return (entity as ProductModel).description;
    } else if (this.isSolutionsEntity(entity)) {
      return (entity as SolutionsModel).summary;
    } else if (this.isPageEntity(entity)) {
      const article = this.getArticle(entity);
      const faq = this.getFaq(entity);
      return article.standfirst || faq.subtitle;
    } else if (this.isVideoEntity(entity)) {
      return (entity as VideoModel).videoDescription;
    }
    return (entity as QuoteModel).artistName;
  };
}

export default ListPanelUtils;
